import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Input,
  Form,
  InputNumber,
  DatePicker,
  Select,
  Switch,
  message,
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { convertFileToBase64 } from "../../../utils/utilFunctions";

const OpenVouchersManager = () => {
  const [openVouchers, setOpenVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchOpenVouchers();
  }, []);

  const fetchOpenVouchers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_URI}/v1/open-vouchers`);
      setOpenVouchers(res.data);
    } catch (error) {
      console.error("Error al obtener open vouchers", error);
      message.error("No se pudieron cargar los open vouchers");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      active: true,
      currency: "EUR",
    });
    setImageFile(null);
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      ...record,
      currency: "EUR",
      expiryDate: record.expiryDate ? dayjs(record.expiryDate) : null,
    });
    setImageFile(null);
    setModalVisible(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "¿Eliminar este open voucher?",
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await axios.delete(`${process.env.REACT_APP_URI}/v1/open-vouchers/${id}`);
          message.success("Open voucher eliminado");
          fetchOpenVouchers();
        } catch (error) {
          console.error("Error al eliminar open voucher", error);
          message.error("No se pudo eliminar");
        }
      },
    });
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    const payload = {
      ...values,
      currency: "EUR",
      expiryDate: values.expiryDate
        ? values.expiryDate.toISOString()
        : undefined,
    };

    if (imageFile instanceof File) {
      payload.image = {
        file: await convertFileToBase64(imageFile),
        filename: imageFile.name,
      };
    }

    try {
      if (editing) {
        await axios.patch(
          `${process.env.REACT_APP_URI}/v1/open-vouchers/${editing.id}`,
          payload
        );
        message.success("Open voucher actualizado");
      } else {
        await axios.post(`${process.env.REACT_APP_URI}/v1/open-vouchers`, payload);
        message.success("Open voucher creado");
      }
      setSubmitting(false);
      setModalVisible(false);
      fetchOpenVouchers();
    } catch (error) {
      console.error("Error al guardar open voucher", error);
      const apiMessage = error.response?.data?.message;
      message.error(apiMessage || "No se pudo guardar");
      setSubmitting(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    setImageFile(file || null);
  };

  const columns = [
    { title: "Nombre", dataIndex: "name", key: "name" },
    { title: "Stripe code", dataIndex: "stripeCode", key: "stripeCode" },
    {
      title: "Importe",
      key: "amount",
      render: (_, record) => `${record.amount} ${record.currency || "EUR"}`,
    },
    {
      title: "Caduca",
      dataIndex: "expiryDate",
      key: "expiryDate",
      render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "—"),
    },
    {
      title: "Activo",
      dataIndex: "active",
      key: "active",
      render: (v) => (v ? "Sí" : "No"),
    },
    { title: "Max usos", dataIndex: "maxRedemptions", key: "maxRedemptions" },
    { title: "Usos", dataIndex: "redeemedCount", key: "redeemedCount" },
    {
      title: "Descripción",
      dataIndex: "description",
      key: "description",
      render: (value) => value || "—",
    },
    {
      title: "Imagen",
      dataIndex: "imageUrl",
      key: "imageUrl",
      render: (value) =>
        value ? (
          <a href={value} target="_blank" rel="noreferrer">
            Ver
          </a>
        ) : (
          "—"
        ),
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_, record) => (
        <>
          <Button type="link" onClick={() => handleEdit(record)}>
            Editar
          </Button>
          <Button type="link" danger onClick={() => handleDelete(record.id)}>
            Eliminar
          </Button>
        </>
      ),
    },
  ];

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between mb-3">
        <h4>Open Vouchers (bonos libres)</h4>
        <Button type="primary" onClick={handleAddNew}>
          + Nuevo open voucher
        </Button>
      </div>

      <Table
        dataSource={openVouchers}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 5 }}
      />

      <Modal
        title={editing ? "Editar open voucher" : "Crear open voucher"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Nombre"
            rules={[{ required: true, message: "Ingresa un nombre" }]}
          >
            <Input placeholder="Ej: Bono Navideño" />
          </Form.Item>

          <Form.Item name="stripeCode" label="Stripe code">
            <Input placeholder="Código de producto/price en Stripe" />
          </Form.Item>

          <Form.Item
            name="amount"
            label="Importe"
            rules={[
              { required: true, message: "Ingresa un importe" },
              { type: "number", min: 0.01, message: "Debe ser mayor a 0" },
            ]}
          >
            <InputNumber
              min={0.01}
              step={0.5}
              className="w-100"
              stringMode={false}
              formatter={(value) =>
                value ? `${value}`.replace(/[^0-9.,-]/g, "") : ""
              }
              parser={(value) =>
                value ? value.replace(/[^\d.-]/g, "") : ""
              }
            />
          </Form.Item>

          <Form.Item name="description" label="Descripción">
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item name="expiryDate" label="Fecha de caducidad">
            <DatePicker className="w-100" format="YYYY-MM-DD" />
          </Form.Item>

          <Form.Item label="Subir imagen (S3)">
            <Input type="file" accept="image/*" onChange={handleImageChange} />
            {editing?.imageUrl && !imageFile && (
              <div className="mt-2">
                <a href={editing.imageUrl} target="_blank" rel="noreferrer">
                  Ver imagen actual
                </a>
              </div>
            )}
          </Form.Item>

          <Form.Item
            name="active"
            label="Activo"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>

          <div className="text-end">
            <Button onClick={() => setModalVisible(false)} style={{ marginRight: 8 }}>
              Cancelar
            </Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {editing ? "Actualizar" : "Crear"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default OpenVouchersManager;

